/* eslint-disable prefer-const */

import { AdvancedFilterSelectedOptions, IconNames } from '@sendible/design-system/src';
import { BrandColors, SemanticColors } from '@sendible/design-system/src/theme';
import { EVENTS, publish } from '@sendible/event-manager';
import { DateTime, FixedOffsetZone } from 'luxon';
import { TFunction } from 'i18next';
import endpoints from '../../data-layer/endpoints';
import { getUrlWithTeamworkCredentialsFromUser } from '../../utils';

const API_VERSION_PATH_PATTERN = /\/v\d+\.\d+\//;

export const stripEndpointVersion = (endpoint: string) => endpoint.replace(API_VERSION_PATH_PATTERN, '/');

const dateTimeFormatterCache = new Map<string, Intl.DateTimeFormat>();

/* Constructing an Intl.DateTimeFormat is expensive, and the calendar formats a label for every day and card it renders
   (the daily view keeps adding days as you scroll). Build each locale/options pair once and reuse it instead of
   constructing one per call. The cache is bounded by the number of distinct locale/options pairs we use. */
export const getDateTimeFormatter = (locale: string | undefined, options: Intl.DateTimeFormatOptions) => {
  const key = `${locale ?? ''}|${JSON.stringify(options)}`;
  let formatter = dateTimeFormatterCache.get(key);

  if (!formatter) {
    formatter = new Intl.DateTimeFormat(locale, options);
    dateTimeFormatterCache.set(key, formatter);
  }

  return formatter;
};

export const DRAGGABLE_STATUSES = ['Draft', 'Pending', 'ForApproval'];

/* Single source of truth for "may this message start a drag".
   The drag source, the cursor affordance and the drop handler all read this, so they can
   never disagree. Recurring messages are never draggable: a series is rescheduled by editing
   its recurrence settings instead. */
export const canDragMessage = (message: Pick<CalendarMessage, 'status' | 'recurrence'>) =>
  !message.recurrence && DRAGGABLE_STATUSES.includes(message.status);

/* A message the recurrence rule alone keeps locked: its status would otherwise allow a drag,
   so attempting one earns an explanation rather than a card that silently refuses to move.
   Statuses that could never be dragged anyway stay silent, since telling someone to reschedule
   an already sent message would be nonsense. */
export const isDragBlockedByRecurrence = (message: Pick<CalendarMessage, 'status' | 'recurrence'>) =>
  !!message.recurrence && DRAGGABLE_STATUSES.includes(message.status);

export const getCalendarThumbnailUrl = (media: CalendarMedia, user: ApiUser) => {
  if (media.twMediaId == null) return media.url;

  return getUrlWithTeamworkCredentialsFromUser(`${endpoints.GetMediaThumbnail.endpoint}/${media.twMediaId}/thumbnail`, user);
};

export const hasCalendarEndpointQueryKey = (queryKey: unknown, endpoint: string) => {
  const [keyEndpoint] = (Array.isArray(queryKey) ? queryKey : []) as [unknown];

  if (typeof keyEndpoint !== 'string') return false;

  const normalizedKey = stripEndpointVersion(keyEndpoint);
  const normalizedEndpoint = stripEndpointVersion(endpoint);

  return normalizedKey === normalizedEndpoint || normalizedKey.startsWith(`${normalizedEndpoint}/`);
};

export const getFailureReasonMessage = (message: string) => {
  if (message.includes('your email address')) {
    return 'failure_reason_invalid_email';
  }

  if (message.includes('daily posting limit')) {
    return 'failure_reason_daily_limit';
  }

  return 'failure_reason_generic';
};

export const networkIconNameMap: Record<string, IconNames> = {
  Facebook: 'facebook_color',
  Instagram: 'instagram_color',
  LinkedIn: 'linkedin_color',
  Pinterest: 'pinterest_color',
  TikTok: 'tiktok_color',
  YouTube: 'youtube_color',
  X: 'x_color',
  Threads: 'threads_color',
  Bluesky: 'bluesky_color',
  BlueSky: 'bluesky_color',
  WordPress: 'wordpress_color',
  GoogleBusiness: 'google_my_business_color',
  Canva: 'canva',
  Giphy: 'giphy',
  Flickr: 'flickr',
  Google: 'google_color',
  'facebook(page)': 'facebook_color',
  instagrampost: 'instagram_color',
  instagrambusiness: 'instagram_color',
  linkedincompany: 'linkedin_color',
  linkedinoauth: 'linkedin_color',
  pinterestoauth: 'pinterest_color',
  tiktokbusiness: 'tiktok_color',
  youtube: 'youtube_color',
  threads: 'threads_color',
  bluesky: 'bluesky_color',
  twitteroauth: 'x_color',
  googlemybusinesslocation: 'google_my_business_color',
  wordpress: 'wordpress_color',
  'wordpress(com)': 'wordpress_color',
};

type RecurrenceUnit = 'D' | 'H' | 'M' | 'W' | 'Y';

const WEEKDAY_NAMES: ReadonlyArray<{ key: string; fallback: string }> = [
  { key: 'monday', fallback: 'Monday' },
  { key: 'tuesday', fallback: 'Tuesday' },
  { key: 'wednesday', fallback: 'Wednesday' },
  { key: 'thursday', fallback: 'Thursday' },
  { key: 'friday', fallback: 'Friday' },
  { key: 'saturday', fallback: 'Saturday' },
  { key: 'sunday', fallback: 'Sunday' },
];

const UNIT_KEY_BY_TYPE: Record<
  RecurrenceUnit,
  { singular: 'day' | 'hour' | 'week' | 'month' | 'year'; plural: 'days' | 'hours' | 'weeks' | 'months' | 'years' }
> = {
  D: { singular: 'day', plural: 'days' },
  H: { singular: 'hour', plural: 'hours' },
  W: { singular: 'week', plural: 'weeks' },
  M: { singular: 'month', plural: 'months' },
  Y: { singular: 'year', plural: 'years' },
};

const parsePattern = (pattern?: string) => {
  if (!pattern) return null;

  const match = pattern.match(/^(\d{2})([DHWMY])([01]{7})?$/);

  if (!match) return null;

  const frequency = Math.max(1, Number(match[1]));
  const unit = match[2] as RecurrenceUnit;
  const mask = match[3] ?? '1111111';

  return { frequency, unit, mask };
};

const getWeekdays = (mask: string, t: TFunction) =>
  mask
    .split('')
    .map((v, idx) => (v === '1' ? idx : -1))
    .filter((idx) => idx >= 0)
    .map((idx) => t(WEEKDAY_NAMES[idx].key, { ns: 'common_terms', defaultValue: WEEKDAY_NAMES[idx].fallback }));

const joinWithLocalizedAnd = (items: string[], andWord: string) => {
  if (items.length <= 1) return items[0] ?? '';
  if (items.length === 2) return `${items[0]} ${andWord} ${items[1]}`;

  return `${items.slice(0, -1).join(', ')} ${andWord} ${items[items.length - 1]}`;
};

const getEveryUnitLabel = (unit: RecurrenceUnit, frequency: number, t: TFunction) => {
  const unitKeys = UNIT_KEY_BY_TYPE[unit];
  const isPlural = frequency !== 1;
  const unitKey = isPlural ? unitKeys.plural : unitKeys.singular;

  const translatedUnit = t(unitKey, {
    ns: 'common_terms',
    count: frequency,
    defaultValue: unitKey,
  });

  const everyLabel = t('every', { ns: 'common_terms', defaultValue: 'every' });

  return isPlural ? `${everyLabel} ${frequency} ${translatedUnit}` : `${everyLabel} ${translatedUnit}`;
};

export const getRecurrenceLabel = (recurrence: Pick<CalendarRecurrence, 'pattern' | 'until' | 'untilLocalized'>, t: TFunction, locale = 'en') => {
  const parsed = parsePattern(recurrence?.pattern);

  const repeatingLabel = t('repeating', { ns: 'common_terms', defaultValue: 'Repeating' });
  const andLabel = t('and', { ns: 'common_terms', defaultValue: 'and' });
  const onlyLabel = t('only', { ns: 'common_terms', defaultValue: 'only' });
  const everyLabel = t('every', { ns: 'common_terms', defaultValue: 'every' });
  const untilLabel = t('until', { ns: 'common_terms', defaultValue: 'until' });

  if (!parsed) return repeatingLabel;

  const weekdays = getWeekdays(parsed.mask, t);
  const allWeekdaysSelected = weekdays.length === 7;
  const hasSpecificWeekdays = weekdays.length > 0 && !allWeekdaysSelected;
  const weekdayList = joinWithLocalizedAnd(weekdays, andLabel);

  let detail = getEveryUnitLabel(parsed.unit, parsed.frequency, t);

  if (parsed.unit === 'D' && hasSpecificWeekdays && parsed.frequency === 1) {
    detail = `${everyLabel} ${weekdayList}`;
  } else if (hasSpecificWeekdays) {
    detail = `${detail} (${weekdayList} ${onlyLabel})`;
  }

  const untilValue = recurrence?.untilLocalized ?? recurrence?.until;

  if (!untilValue) return `${repeatingLabel} ${detail}`;

  const untilDate = new Date(untilValue);

  if (Number.isNaN(untilDate.getTime())) return `${repeatingLabel} ${detail}`;

  const formattedUntil = getDateTimeFormatter(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(untilDate);

  return `${repeatingLabel} ${detail} ${untilLabel} ${formattedUntil}`;
};

export const processTags = (text: string): string => {
  const mentionRegex = /@\[(?:li_|ig_|th_|fb_|bs_|tw_|liperson_)([^:\]]+)(?::\d+)?\]/g;
  const anchorRegex = /<a class="[^"]*-tag-it"[^>]*>@([^<]+)<\/a>/g;

  return (text ?? '').replace(mentionRegex, '@$1').replace(anchorRegex, '@$1');
};

// URL pattern: matches http(s) and bare www. links
export const URL_REGEX = /(https?:\/\/[^\s<>"']+|www\.[^\s<>"']+)/g;
export function transformLinks<T>(text: string, renderText: (s: string) => T, renderLink: (href: string, display: string, key: number) => T): T[] {
  const parts = text.split(URL_REGEX);

  return parts.map((part, i) => {
    URL_REGEX.lastIndex = 0;

    if (!URL_REGEX.test(part)) {
      URL_REGEX.lastIndex = 0;

      return renderText(part);
    }

    URL_REGEX.lastIndex = 0;

    const href = part.startsWith('http') ? part : `https://${part}`;

    return renderLink(href, part, i);
  });
}

export function renderLinksToHtml(text: string): string {
  return transformLinks<string>(
    text,
    (s) => s,
    (href, display) => {
      const safeHref = href.replace(/"/g, '&quot;');
      const safeDisplay = display.replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' })[c] as string);

      return `<a href="${safeHref}" target="_blank" rel="noopener noreferrer">${safeDisplay}</a>`;
    }
  ).join('');
}
export const timeToSeconds = (time: string): number => {
  const [hh, mm, ss = '0'] = time.split(':');

  return Number(hh) * 3600 + Number(mm) * 60 + Number(ss);
};

export const compareTimes = (a: string, b: string) => timeToSeconds(a) - timeToSeconds(b);

export const computeRescheduledSendDate = (params: {
  newDate: string;
  messageTime: string;
  timezoneOffsetMinutes: number;
  now: DateTime;
}): CalendarMessage['sendDateLocalized'] => {
  const { newDate, messageTime, timezoneOffsetMinutes, now } = params;

  const zone = FixedOffsetZone.instance(timezoneOffsetMinutes);

  const baseNow = now;
  const userNow = baseNow.setZone(zone);
  const userToday = userNow.toISODate();
  const isToday = Boolean(userToday) && newDate === userToday;

  if (!isToday) return `${newDate}T${messageTime}` as CalendarMessage['sendDateLocalized'];

  const userTimeNow = userNow.toFormat('HH:mm:ss');
  const isPastTime = compareTimes(messageTime, userTimeNow) < 0;

  if (!isPastTime) return `${newDate}T${messageTime}` as CalendarMessage['sendDateLocalized'];

  const adjusted = userNow.plus({ minutes: 10 });

  return `${adjusted.toISODate()}T${adjusted.toFormat('HH:mm:ss')}` as CalendarMessage['sendDateLocalized'];
};

const extractYMD = (iso: string): [number, number, number] | null => {
  const parts = iso.split('-');

  if (parts.length !== 3) return null;

  const [yStr, mStr, dStr] = parts;
  const y = Number(yStr);
  const m0 = Number(mStr) - 1;
  const d = Number(dStr);

  if ([y, m0, d].some(Number.isNaN)) return null;

  return [y, m0, d];
};

export function getDaysInMonth(year: number, month: number) {
  return new Date(year, month + 1, 0).getDate();
}

export const monthStart = (d: Date) => new Date(d.getFullYear(), d.getMonth(), 1);
export const monthEnd = (d: Date) => new Date(d.getFullYear(), d.getMonth() + 1, 0);
export const addMonths = (d: Date, delta: number) => new Date(d.getFullYear(), d.getMonth() + delta, 1);

export function getFirstDayOfWeek(year: number, month: number, weekStart: UserWeekStartDay) {
  const firstDay = new Date(year, month, 1).getDay();

  if (weekStart === 'monday') {
    return firstDay === 0 ? 6 : firstDay - 1;
  }

  return firstDay;
}

export const removePausedQueuesFromSelectedFilters = (
  selected: AdvancedFilterSelectedOptions[],
  pausedQueuesIds?: Array<string | number> | null
): { cleaned: AdvancedFilterSelectedOptions[]; didRemove: boolean } => {
  if (!Array.isArray(selected) || selected.length === 0) return { cleaned: [], didRemove: false };
  if (!pausedQueuesIds || pausedQueuesIds.length === 0) return { cleaned: selected, didRemove: false };

  const pausedSet = new Set(pausedQueuesIds.map((id) => String(id)));
  let didRemove = false;

  const cleaned = selected
    .map((group) => {
      if (group?.id !== 'queues') return group;

      const nextSelected = (group.selected ?? []).filter((id) => !pausedSet.has(String(id)));

      if (nextSelected.length !== (group.selected ?? []).length) didRemove = true;

      if (nextSelected.length === 0) return null;

      return {
        ...group,
        selected: nextSelected,
      };
    })
    .filter(Boolean) as AdvancedFilterSelectedOptions[];

  return { cleaned, didRemove };
};

export const keyFromDate = (dt: Date): CalendarMonthString =>
  `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}` as CalendarMonthString;

export const getChannelIcon = (channel: MessageChannels): IconNames | undefined => {
  if (channel === 'facebook(page)') return 'facebook_color';
  if (channel === 'instagrampost' || channel === 'instagrambusiness') return 'instagram_color';
  if (channel === 'twitteroauth') return 'x_color';
  if (channel === 'linkedincompany' || channel === 'linkedinoauth') return 'linkedin_color';
  if (channel === 'googlemybusinesslocation') return 'google_my_business_color';
  if (channel === 'youtube') return 'youtube_color';
  if (channel === 'pinterestoauth') return 'pinterest_color';
  if (channel === 'tiktokbusiness') return 'tiktok_color';
  if (channel === 'threads') return 'threads_color';
  if (channel === 'bluesky') return 'bluesky_color';
  if (channel === 'rss(auto)') return 'rss_color';
  if (channel.includes('wordpress')) return 'wordpress_color';
  if (channel === 'Group') return 'groups_color';
};

export const getStatusIcon = (
  status: CalendarStatuses,
  isRecurring: boolean
): { icon: IconNames; color: BrandColors | SemanticColors } | undefined => {
  if (isRecurring) return { icon: 'refresh_cw', color: 'semanticInfo' };
  if (status === 'Bounced' || status === 'Failed') return { icon: 'danger', color: 'semanticDanger' };
  if (status === 'Draft') return { icon: 'edit_alt', color: 'brandDarkTint30' };
  if (status === 'Rejected') return { icon: 'chat_cross', color: 'semanticDanger' };
  if (status === 'Approved' || status === 'Pending') return { icon: 'clock', color: 'semanticInfo' };
  if (status === 'Queued') return { icon: 'queue', color: 'semanticInfo' };
  if (status === 'Sent') return { icon: 'check', color: 'semanticSuccessShade10' };
  if (status === 'ForApproval') return { icon: 'chat', color: 'brandDarkTint30' };
};

export const getBouncedPercent = (deliveryStatus: CalendarMessage['deliveryStatus']): number => {
  const total = Number(deliveryStatus?.total ?? 0);
  const failed = Number(deliveryStatus?.failed ?? 0);

  if (!Number.isFinite(total) || total <= 0) return 0;

  const pct = (failed / total) * 100;

  return Math.max(0, Math.min(Math.round(pct), 100));
};

export const getDeliveryStatusIcon = (
  status: CalendarStatuses,
  isRecurring: boolean,
  deliveryStatus: CalendarMessage['deliveryStatus'],
  isPartialModeSuccess = false
): { icon: IconNames; color: BrandColors | SemanticColors; progress?: { percent: number; type: 'red' | 'green' } } | undefined => {
  const bouncedPercentage = getBouncedPercent(deliveryStatus);
  const hasBouncedMessages = bouncedPercentage > 0;
  const allMessagesBounced = bouncedPercentage === 100;
  const isPartiallyBounced = hasBouncedMessages && !allMessagesBounced;
  const clamp = (pct: number) => (pct > 0 && pct < 10 ? 10 : pct);

  if (hasBouncedMessages) {
    const icon = (isPartialModeSuccess ? 'check_small' : allMessagesBounced ? 'danger' : 'exclamation') as IconNames;
    const color = (isPartialModeSuccess ? 'semanticSuccessShade10' : 'semanticDanger') as BrandColors | SemanticColors;
    const progress = isPartiallyBounced
      ? ({
          percent: isPartialModeSuccess ? clamp(100 - bouncedPercentage) : clamp(bouncedPercentage),
          type: isPartialModeSuccess ? 'green' : 'red',
        } as const)
      : undefined;

    return { icon, color, progress };
  }

  return getStatusIcon(status, isRecurring);
};

export const getIsPartialModeSuccess = (filterParams?: Record<string, string>): boolean => {
  const isSentFilterSelected = filterParams?.statuses?.includes('Sent');
  const isBouncedFilterSelected = filterParams?.statuses?.includes('Bounced');

  return !!isSentFilterSelected && !isBouncedFilterSelected;
};

export const getBounceStatus = (deliveryStatus: CalendarMessage['deliveryStatus']) => {
  const bouncedPercentage = getBouncedPercent(deliveryStatus);
  const allMessagesBounced = bouncedPercentage === 100;
  const isPartiallyBounced = bouncedPercentage > 0 && !allMessagesBounced;

  return { bouncedPercentage, allMessagesBounced, isPartiallyBounced };
};

export const getDeliveryStatusTooltip = ({
  recurrence,
  deliveryStatus,
  status,
  isPartialModeSuccess,
  isPaused,
  t,
  language,
}: {
  recurrence?: CalendarMessage['recurrence'];
  deliveryStatus: CalendarMessage['deliveryStatus'];
  status: CalendarStatuses;
  isPartialModeSuccess: boolean;
  isPaused?: boolean;
  t: TFunction;
  language: string;
}): string => {
  const { allMessagesBounced, isPartiallyBounced } = getBounceStatus(deliveryStatus);

  if (recurrence) return getRecurrenceLabel(recurrence, t, language);
  if (allMessagesBounced) return t('Failed');
  if (isPartiallyBounced) {
    if (isPartialModeSuccess) return `${deliveryStatus.failed} ${t('Sent')}`;

    return `${deliveryStatus.failed} ${t('Failed')}`;
  }

  const shouldShowPausedLabel = isPaused && status !== 'Sent' && status !== 'Failed';

  return shouldShowPausedLabel ? `${t(status)} (${t('paused')})` : t(status);
};

export const getDeliveryStatusLabel = ({
  deliveryStatus,
  status,
  isPartialModeSuccess,
  isAssignedToMe,
  t,
}: {
  deliveryStatus: CalendarMessage['deliveryStatus'];
  status: CalendarStatuses;
  isPartialModeSuccess: boolean;
  isAssignedToMe?: boolean;
  t: TFunction;
}): string => {
  const { allMessagesBounced, isPartiallyBounced } = getBounceStatus(deliveryStatus);

  if (allMessagesBounced) return t('Failed');
  if (isPartiallyBounced) {
    if (isPartialModeSuccess) return `${deliveryStatus.failed} ${t('Sent')}`;

    return `${deliveryStatus.failed} ${t('Failed')}`;
  }
  if (isAssignedToMe) return t('to_review');

  return t(status);
};

export const getTodayISO = (timezoneOffsetMinutes = 0): CalendarMonthString => {
  // Shift UTC now by the user's timezone offset, then read back UTC fields
  // to get the date as the user sees it in their timezone.
  const dt = new Date(Date.now() + timezoneOffsetMinutes * 60_000);
  const y = String(dt.getUTCFullYear()).padStart(4, '0');
  const m = String(dt.getUTCMonth() + 1).padStart(2, '0');
  const d = String(dt.getUTCDate()).padStart(2, '0');

  return `${y}-${m}-${d}` as CalendarMonthString;
};

export const startOfWeek = (targetISO: CalendarMonthString, weekStart: UserWeekStartDay): Date => {
  const [y, m, d] = targetISO.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  const day = dt.getDay(); // 0 Sun .. 6 Sat
  const offset = weekStart === 'monday' ? (day === 0 ? 6 : day - 1) : day;

  dt.setDate(dt.getDate() - offset);

  return dt;
};

export const toUtcMidnightDate = (day: CalendarMonthString) => new Date(`${day}T00:00:00.000Z`);

export const formatYear = (isoDay: string) => {
  if (!isoDay) return '';

  const [y] = isoDay.split('-');

  return y;
};

export const formatMonthYear = (isoDay: string, userLocale: string) => {
  if (!isoDay) return '';

  const [y, m] = isoDay.split('-');
  const year = Number(y);
  const month = Number(m);

  if (Number.isNaN(year) || Number.isNaN(month)) return isoDay;

  const dt = new Date(year, month - 1, 1);

  return getDateTimeFormatter(userLocale, { month: 'long', year: 'numeric' }).format(dt);
};

export const shiftMonth = (iso: CalendarMonthString, delta: number): CalendarMonthString => {
  const parts = extractYMD(iso);

  if (!parts || !delta) return iso;

  let [year, month0, day] = parts;

  month0 += delta;
  if (month0 > 11) {
    year += Math.floor(month0 / 12);
    month0 %= 12;
  } else if (month0 < 0) {
    const dec = Math.ceil(Math.abs(month0) / 12);

    year -= dec;
    month0 = ((month0 % 12) + 12) % 12;
  }

  const daysInTarget = new Date(year, month0 + 1, 0).getDate();
  const clampedDay = Math.min(day, daysInTarget);

  return `${String(year).padStart(4, '0')}-${String(month0 + 1).padStart(2, '0')}-${String(clampedDay).padStart(2, '0')}` as CalendarMonthString;
};

export const shiftWeek = (iso: CalendarMonthString, delta: number): CalendarMonthString => {
  if (!delta) return iso;

  const parts = extractYMD(iso);

  if (!parts) return iso;

  const [y, m0, d] = parts;
  const dt = new Date(y, m0, d);

  dt.setDate(dt.getDate() + delta * 7);

  return `${String(dt.getFullYear()).padStart(4, '0')}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(
    2,
    '0'
  )}` as CalendarMonthString;
};

export const shiftDay = (iso: CalendarMonthString, delta: number): CalendarMonthString => {
  if (!delta) return iso;

  const parts = extractYMD(iso);

  if (!parts) return iso;

  const [y, m0, d] = parts;
  const dt = new Date(y, m0, d);

  dt.setDate(dt.getDate() + delta);

  return `${String(dt.getFullYear()).padStart(4, '0')}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(
    2,
    '0'
  )}` as CalendarMonthString;
};

export const getMonthRange = (isoDay: CalendarMonthString, weekStart: UserWeekStartDay): [CalendarMonthString, CalendarMonthString] => {
  const parts = extractYMD(isoDay);

  if (!parts) return [isoDay, isoDay];

  const [year, month0] = parts;

  const firstMonthDate = new Date(year, month0, 1);
  const lastMonthDate = new Date(year, month0 + 1, 0);

  const mondayIndex = (d: number) => (d === 0 ? 6 : d - 1);

  const firstDow = firstMonthDate.getDay();
  const offsetToWeekStart = weekStart === 'monday' ? mondayIndex(firstDow) : firstDow;
  const calendarStart = new Date(firstMonthDate);

  calendarStart.setDate(firstMonthDate.getDate() - offsetToWeekStart);

  const lastDow = lastMonthDate.getDay();
  const endOffset = weekStart === 'monday' ? 6 - mondayIndex(lastDow) : 6 - lastDow;
  const calendarEnd = new Date(lastMonthDate);

  calendarEnd.setDate(lastMonthDate.getDate() + endOffset);

  return [keyFromDate(calendarStart), keyFromDate(calendarEnd)];
};

export const getWeekRange = (isoDay: CalendarMonthString, weekStart: UserWeekStartDay): [CalendarMonthString, CalendarMonthString] => {
  const parts = extractYMD(isoDay);

  if (!parts) return [isoDay, isoDay];

  const [y, m0, d] = parts;

  const target = new Date(y, m0, d);
  const dayOfWeek = target.getDay();

  const offsetToStart = weekStart === 'monday' ? (dayOfWeek === 0 ? -6 : -(dayOfWeek - 1)) : -dayOfWeek;

  const start = new Date(target);

  start.setDate(target.getDate() + offsetToStart);

  const end = new Date(start);

  end.setDate(start.getDate() + 6);

  return [keyFromDate(start), keyFromDate(end)];
};

export function formatWeekRange(a: CalendarMonthString, b: CalendarMonthString | UserWeekStartDay, userLocale: string): string {
  const isWeekStart = (val: unknown): val is UserWeekStartDay => val === 'monday' || val === 'sunday';

  let startISO: CalendarMonthString;
  let endISO: CalendarMonthString;

  if (isWeekStart(b)) {
    [startISO, endISO] = getWeekRange(a, b);
  } else {
    startISO = a;
    endISO = b;
  }

  const startParts = extractYMD(startISO);
  const endParts = extractYMD(endISO);

  if (!startParts || !endParts) return `${startISO} - ${endISO}`;

  const [sy, sm0, sd] = startParts;
  const [ey, em0, ed] = endParts;
  const start = new Date(sy, sm0, sd);
  const end = new Date(ey, em0, ed);

  const monthFormatter = getDateTimeFormatter(userLocale, { month: 'short' });
  const dayFormatter = getDateTimeFormatter(userLocale, { day: 'numeric' });
  const yearFormatter = getDateTimeFormatter(userLocale, { year: 'numeric' });

  const sameMonth = start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear();
  const sameYear = start.getFullYear() === end.getFullYear();

  if (sameMonth) {
    return `${monthFormatter.format(start)} ${dayFormatter.format(start)} - ${dayFormatter.format(end)}, ${yearFormatter.format(start)}`;
  }
  if (sameYear) {
    return `${monthFormatter.format(start)} ${dayFormatter.format(start)} - ${monthFormatter.format(end)} ${dayFormatter.format(
      end
    )}, ${yearFormatter.format(start)}`;
  }

  return `${monthFormatter.format(start)} ${dayFormatter.format(start)}, ${yearFormatter.format(start)} - ${monthFormatter.format(
    end
  )} ${dayFormatter.format(end)}, ${yearFormatter.format(end)}`;
}

export const addDaysISO = (iso: CalendarMonthString, delta: number): CalendarMonthString => {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y, m - 1, d + delta);

  return `${dt.getFullYear()}-${`${dt.getMonth() + 1}`.padStart(2, '0')}-${`${dt.getDate()}`.padStart(2, '0')}` as CalendarMonthString;
};

/* Day heading for the daily view, e.g. { dayNum: 12, month: 'AUG', weekday: 'MON' }. Month and weekday are the
   short names in the user's locale (SEN-9662: the numeric month it was showing read as a second day number). */
export const formatDayLabel = (iso: CalendarMonthString, userLocale: string | undefined) => {
  const [y, m, d] = iso.split('-').map(Number);
  const date = new Date(y, m - 1, d);

  const month = getDateTimeFormatter(userLocale, { month: 'short' }).format(date).replace(/\.$/, '').toUpperCase();
  // Cut at three characters by code point so a long name is never split mid-character. Shorter names (ja, zh, de)
  // are left as they are rather than padded with spaces.
  const weekday = Array.from(getDateTimeFormatter(userLocale, { weekday: 'short' }).format(date).replace(/\.$/, '').toUpperCase())
    .slice(0, 3)
    .join('');

  return { dayNum: d, month, weekday };
};

export const generateDays = (start: Date, end: Date): CalendarMonthString[] => {
  const out: CalendarMonthString[] = [];
  const cursor = new Date(start);

  while (cursor <= end) {
    out.push(keyFromDate(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }

  return out;
};

export const getNewPostTime = (): string => {
  const nowUtcMs = Date.now();
  const plusFifteenMinutesMs = 15 * 60 * 1000;
  const shifted = new Date(nowUtcMs + plusFifteenMinutesMs);
  const hh = String(shifted.getHours()).padStart(2, '0');
  const mm = String(shifted.getMinutes()).padStart(2, '0');

  return `${hh}:${mm}`;
};

export const editMessage = (status: CalendarStatuses, id: number, date: string, offset: number, recipientId?: number) => {
  if (status === 'Sent') {
    return publish(EVENTS.OPEN_COMPOSE, { mode: 'repost', id, date, offset, recipientId });
  }

  if (status === 'Draft' || status === 'Pending' || status === 'Queued') {
    return publish(EVENTS.OPEN_COMPOSE, { mode: 'edit', id, date, offset });
  }

  if (status === 'Rejected') {
    return publish(EVENTS.OPEN_COMPOSE, { mode: 'resubmit', id, date, offset });
  }

  if (status === 'ForApproval') {
    return publish(EVENTS.OPEN_COMPOSE, { mode: 'task', id, date, offset });
  }
};

export const formatISOToLong = (iso: string, locale: string) => {
  if (!iso) return '';

  const d = new Date(iso);

  return getDateTimeFormatter(locale, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(d);
};

export const formatTimeLabelNoTransform = (input: string, clockFormat: 12 | 24 = 12): string => {
  const timeFormat = clockFormat === 24 ? 'HH:mm' : 'h:mm a';
  const timeOnly = /^(\d{2}):(\d{2})$/.exec(input);

  if (timeOnly) {
    const hours = Number(timeOnly[1]);
    const minutes = Number(timeOnly[2]);

    const dt = DateTime.utc(1970, 1, 1, hours, minutes, 0);

    return dt.toFormat(timeFormat);
  }

  let dt = DateTime.fromISO(input.replace(' ', 'T'), { zone: 'utc' });

  if (!dt.isValid) {
    const js = new Date(input);

    if (Number.isNaN(js.getTime())) return input;
    dt = DateTime.fromJSDate(js, { zone: 'utc' });
  }

  return dt.toFormat(timeFormat);
};

export const fullDateToMonthString = (fullDate: CalendarFullDateString): CalendarMonthString => fullDate.split('T')[0] as CalendarMonthString;

export const formatSendDate = (iso: CalendarFullDateString, locale: string, clockFormat: 12 | 24 = 12): string => {
  const normalized = iso.replace(' ', 'T');
  const date = new Date(normalized);

  if (Number.isNaN(date.getTime())) return iso;

  const datePart = getDateTimeFormatter(locale, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(date);

  const timePart = formatTimeLabelNoTransform(iso, clockFormat);

  return `${datePart} - ${timePart}`;
};

export const groupHolidaysByDate = (holidays: Holiday[] | undefined | null) => {
  const map: Record<string, Holiday[]> = {};

  if (!Array.isArray(holidays)) return map;

  for (const h of holidays) {
    if (!h?.date) continue;

    if (!map[h.date]) map[h.date] = [];
    map[h.date].push(h);
  }

  return map;
};

export const isPdfMedia = (media: Pick<CalendarMedia, 'url' | 'type'>): boolean => {
  const url = media?.url ?? '';
  const type = (media?.type ?? '').toLowerCase();

  return type === 'pdf' || type === 'file' || type === 'document' || /\.pdf(\?|#|$)/i.test(url);
};

export const getCalendarCardMedias = (body: CalendarMessage['body'] | undefined): CalendarMedia[] => {
  const medias = body?.media ?? [];
  const previewUrl = body?.linkPreview?.previewUrl;

  if (!previewUrl) return medias;

  const linkPreviewMedia: CalendarMedia = {
    id: -1,
    type: 'Image',
    url: previewUrl,
    isLinkPreview: true,
  };

  return [...medias, linkPreviewMedia];
};

// The date the daily view opens on when it replaces the weekly or monthly one. Those move the current date by a
// week or a month at a time, so it is only some day inside the period they show. The daily view starts at the
// first day of that period, or at today when the period is the one that holds today.
export const dailyStartDate = (
  fromView: CalendarView,
  currentDate: CalendarMonthString,
  todayDate: CalendarMonthString,
  weekStart: UserWeekStartDay
): CalendarMonthString => {
  if (fromView === 'weekly') {
    const [start, end] = getWeekRange(currentDate, weekStart);

    return todayDate >= start && todayDate <= end ? todayDate : start;
  }

  if (fromView === 'monthly') {
    const month = currentDate.slice(0, 7);

    return todayDate.slice(0, 7) === month ? todayDate : (`${month}-01` as CalendarMonthString);
  }

  return currentDate;
};
